import {createContext, PropsWithChildren, ReactElement, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IStore} from "../redux/defaultStore";
import {io, Socket} from "socket.io-client";
import { WebsocketEvent } from "@devour/client";

interface WebsocketProviderContextType {
    socket?: Socket;
    socketConnection: boolean;
}

const initialWebsocketContext: WebsocketProviderContextType = {
    socket: undefined,
    socketConnection: false,
};

export const WebsocketProviderContext = createContext<WebsocketProviderContextType>(initialWebsocketContext);

function WebsocketProvider(props: PropsWithChildren): ReactElement {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const socket = useRef<Socket>(null);
    const [socketConnection, setSocketConnection] = useState<boolean>(false);


    useEffect(() => {
        // Don't create socket if no token available
        if (!fullToken) {
            return;
        }

        const socketUrl = "https://staging1-backend-socket-360130971828.us-central1.run.app";

        socket.current = io(socketUrl, {
            reconnection: true,
            reconnectionDelay: 1000, // Reduced from 10000 for faster recovery
            reconnectionDelayMax: 5000, // Cap max delay at 5 seconds
            reconnectionAttempts: 100,
            auth: {
                token: fullToken, // Keep existing format - fullToken is the correct object
            },
        });
        socket.current.on("connect", () => {
            setSocketConnection(true);
        });
        socket.current.on("disconnect", (reason) => {
            setSocketConnection(false);
        });
        socket.current.on("connect_error", (error) => {
            // Keep minimal error logging for actual issues
            console.error("[WebSocket] Connection error:", error.message);
        });
        socket.current.on(WebsocketEvent.RECONNECT, () => {
            socket.current?.close();
            socket.current?.connect();
        });

        // Handle page visibility changes (laptop sleep/wake, tab switching)
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                // Force reconnect if not connected
                if (!socket.current?.connected) {
                    socket.current?.connect();
                }
            }
        };

        // Handle online/offline events
        const handleOnline = () => {
            if (!socket.current?.connected) {
                socket.current?.connect();
            }
        };

        const handleOffline = () => {
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        // Also check on focus (handles some edge cases)
        window.addEventListener("focus", handleVisibilityChange);

        return () => {
            // Disconnect from socket connection on component unmount
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("focus", handleVisibilityChange);
            socket.current?.close();
            socket.current = null;
            setSocketConnection(false);
        };
    }, [fullToken?.id]);

    return (
        <WebsocketProviderContext.Provider value={{
            socket: socket.current,
            socketConnection: socketConnection,
        }}>
            {props.children}
        </WebsocketProviderContext.Provider>
    );
}

export default WebsocketProvider;
